import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  CheckCircle2, 
  Calendar, 
  Clock,
  Search,
  Filter,
  FileText,
  TrendingUp,
  Eye,
  ArrowUpDown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Engagement Badge
const EngagementBadge = ({ prediction }) => {
  if (!prediction) return null;
  
  const colors = {
    HIGH: 'bg-[#d8e5b8] text-[#3d4f22]',
    MEDIUM: 'bg-amber-100 text-amber-700',
    LOW: 'bg-red-100 text-red-700'
  };
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${colors[prediction.score] || colors.MEDIUM}`}>
      <TrendingUp className="w-3 h-3" />
      {prediction.score}
    </div>
  );
};

const ApprovedPosts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  useEffect(() => {
    fetchApprovedPosts();
  }, []);

  useEffect(() => {
    filterAndSortPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, searchQuery, statusFilter, sortOrder]);

  const fetchApprovedPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/client/approved-posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch approved posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPosts = () => {
    let result = [...posts];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(post => 
        post.copy?.toLowerCase().includes(query) ||
        post.post_type?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(post => {
        const status = post.approval_stage || post.status;
        return status === statusFilter;
      });
    }

    // Sort
    result.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredPosts(result);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      approved: 'bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]',
      scheduled: 'bg-purple-100 text-purple-700 border-purple-200',
      posted: 'bg-green-100 text-green-700 border-green-200'
    };
    return classes[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'scheduled':
        return <Clock className="w-4 h-4" />;
      case 'posted':
        return <CheckCircle2 className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d4f22]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approved-posts">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Approved Posts
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {filteredPosts.length} approved post{filteredPosts.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Stats Summary */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {posts.filter(p => (p.approval_stage || p.status) === 'approved').length} Approved
            </Badge>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
              <Clock className="w-3 h-3 mr-1" />
              {posts.filter(p => (p.approval_stage || p.status) === 'scheduled').length} Scheduled
            </Badge>
            <Badge className="bg-green-100 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {posts.filter(p => (p.approval_stage || p.status) === 'posted').length} Posted
            </Badge>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="sort-order">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              {searchQuery || statusFilter !== 'all' 
                ? 'No posts match your filters'
                : 'No Approved Posts Yet'
              }
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Posts you approve will appear here'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map((post) => {
            const status = post.approval_stage || post.status;
            return (
              <Card 
                key={post.id}
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-[#91a85e] transition-all cursor-pointer"
                onClick={() => navigate(`/client/posts/${post.id}`)}
                data-testid={`post-card-${post.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge className={`${getStatusBadgeClass(status)} border`}>
                      {getStatusIcon(status)}
                      <span className="ml-1">{formatStatus(status)}</span>
                    </Badge>
                    {post.engagement_prediction && (
                      <EngagementBadge prediction={post.engagement_prediction} />
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {/* Post Preview */}
                  <div className="text-sm text-slate-700 dark:text-slate-300 line-clamp-4">
                    {post.copy}
                  </div>

                  {/* Thumbnail */}
                  {post.image_urls?.length > 0 && (
                    <img
                      src={post.image_urls[0]}
                      alt=""
                      className="w-full h-24 object-cover rounded-lg bg-slate-100"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <span className="capitalize flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {post.post_type?.replace(/_/g, ' ')}
                    </span>
                    
                    {post.scheduled_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(post.scheduled_date), 'MMM d')}
                      </span>
                    )}
                  </div>

                  {/* Approval Date */}
                  <p className="text-xs text-[#3d4f22] dark:text-[#b8cf88] flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Approved {format(new Date(post.updated_at || post.created_at), 'MMM d, yyyy')}
                  </p>

                  {/* View Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2"
                    data-testid={`view-post-${post.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovedPosts;
